import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Strings } from '../constants/Strings';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

import type { EventFilterType, Gathering } from '@cobu/types';
import {
  getGatheringsQuery,
  getPastGatheringsQuery,
  checkInMutation as createCheckInMutation,
  updateCheckInMutation as createUpdateCheckInMutation,
  unCheckInMutation as createUnCheckInMutation,
} from '@cobu/api-client';
import { isAfterCurrentTime, isBeforeCurrentTime } from '../utils/dateTime';

// Event filter configuration — labels resolved lazily via Strings proxy
export const EVENT_FILTERS: { filterType: EventFilterType; get filterText(): string }[] = [
  { filterType: 'upcoming', get filterText() { return Strings.get().events.filters.upcoming; } },
  { filterType: 'attending', get filterText() { return Strings.get().events.filters.attending; } },
  { filterType: 'past', get filterText() { return Strings.get().events.filters.past; } },
];

/**
 * Comprehensive events hook with filtering and business logic
 */
export const useEvents = (buildingUuid: string) => {
  const [currentFilter, setCurrentFilter] = useState<EventFilterType>('upcoming');
  const [hasLoadedPastGatherings, setHasLoadedPastGatherings] = useState(false);
  const navigate = useNavigate();

  const trackEvent = useCallback((name: string, props?: Record<string, unknown>) => {
    try {
      (window as any).analyticsService?.trackEvent?.(name, props);
    } catch {}
  }, []);

  // Queries
  const gatheringsQuery = getGatheringsQuery(buildingUuid);
  const pastGatheringsQuery = getPastGatheringsQuery(buildingUuid, hasLoadedPastGatherings);

  // Mutations
  const checkInMut = createCheckInMutation();
  const updateCheckInMut = createUpdateCheckInMutation();
  const unCheckInMut = createUnCheckInMutation();

  /**
   * Get filtered gatherings based on current filter with count
   */
  const getFilteredGatherings = useCallback(() => {
    const gatherings = gatheringsQuery.data || [];
    const pastGatherings = pastGatheringsQuery.data || [];

    let filteredResults: Gathering[] = [];
    
    if (currentFilter === 'attending') {
      filteredResults = gatherings.filter(gathering => gathering.isAttending);
    } else if (currentFilter === 'past') {
      filteredResults = pastGatherings;
    } else if (currentFilter === 'upcoming') {
      filteredResults = gatherings.filter(gathering => !gathering.cancelled);
    }

    return {
      gatherings: filteredResults,
      count: filteredResults.length,
    };
  }, [gatheringsQuery.data, pastGatheringsQuery.data, currentFilter]);

  /**
   * Handle filter press with analytics tracking
   */
  const handleFilterPress = useCallback((filter: EventFilterType) => {
    if (filter === currentFilter) {
      return;
    }
    trackEvent('SELECT_EVENT_FILTER', { filter });

    if (filter === 'past' && !hasLoadedPastGatherings) {
      setHasLoadedPastGatherings(true);
    }

    setCurrentFilter(filter);
  }, [currentFilter, hasLoadedPastGatherings]);

  /**
   * Check if an event is currently happening
   */
  const isEventLive = useCallback((gathering: Gathering) => {
    const startTime = dayjs(gathering.scheduledStartTime).tz(gathering.buildingTimezone);
    const isRecurringOrPartner = !!gathering.recurringType || !!gathering.isPartnerEvent;
    const endTime = gathering.scheduledEndTime
      ? dayjs(gathering.scheduledEndTime).tz(gathering.buildingTimezone)
      : isRecurringOrPartner
        ? dayjs(gathering.scheduledStartTime).tz(gathering.buildingTimezone).add(1, 'hour')
        : null;

    if (!endTime) return false;
    return isAfterCurrentTime(startTime) && isBeforeCurrentTime(endTime);
  }, []);

  /**
   * Refresh gatherings based on current filter
   */
  const refreshEvents = useCallback(async () => {
    // trackEvent('REFRESH_EVENT_FEED', { filter: currentFilter });

    switch (currentFilter) {
      case 'upcoming':
      case 'attending':
        await gatheringsQuery.refetch();
        break;
      case 'past':
        await pastGatheringsQuery.refetch();
        break;
      default:
        break;
    }
  }, [currentFilter, gatheringsQuery, pastGatheringsQuery]);

  /**
   * Open notification center
   */
  const openNotificationCenter = useCallback(() => {
    trackEvent('VIEW_NOTIFICATION_CENTER', { source: 'events_screen' });
    navigate('/notifications');
  }, [navigate, trackEvent]);

  /**
   * Get empty state configuration based on current filter
   */
  const getEmptyStateConfig = useCallback(() => {
    const empty = Strings.get().events.empty;
    let title = '';
    let description = '';

    if (currentFilter === 'attending') {
      title = empty.attendingTitle;
      description = empty.attendingSubtitle;
    } else if (currentFilter === 'past') {
      title = empty.pastTitle;
      description = empty.pastSubtitle;
    } else {
      title = empty.upcomingTitle;
      description = empty.upcomingSubtitle;
    }

    return { title, description };
  }, [currentFilter]);

  const { gatherings: filteredGatherings, count: filteredGatheringsCount } = getFilteredGatherings();

  return {
    // Data
    gatherings: gatheringsQuery.data || [],
    pastGatherings: pastGatheringsQuery.data || [],
    filteredGatherings,
    filteredGatheringsCount,
    
    // Filter configuration
    filters: EVENT_FILTERS,
    
    // Filter state
    currentFilter,
    setCurrentFilter: handleFilterPress,
    
    // Loading states
    isLoading: gatheringsQuery.isLoading || pastGatheringsQuery.isLoading,
    isRefreshing: gatheringsQuery.isFetching || pastGatheringsQuery.isFetching,
    
    // Error states
    isError: gatheringsQuery.isError || pastGatheringsQuery.isError,
    error: gatheringsQuery.error || pastGatheringsQuery.error,
    
    // Actions
    refreshEvents,
    checkIn: checkInMut.mutateAsync,
    updateCheckIn: updateCheckInMut.mutateAsync,
    unCheckIn: unCheckInMut.mutateAsync,
    openNotificationCenter,
    
    // Mutation states
    isCheckingIn: checkInMut.isPending,
    isUpdatingCheckIn: updateCheckInMut.isPending,
    isUnCheckingIn: unCheckInMut.isPending,
    
    // Utility functions
    isEventLive,
    getEmptyStateConfig,
    
    // Query functions
    refetchGatherings: gatheringsQuery.refetch,
    refetchPastGatherings: pastGatheringsQuery.refetch,
  };
}; 